import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatRoleName } from '../../../utils/roleFormatter';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './UserMenu.css';

function getInitials(fullName) {
  if (!fullName || typeof fullName !== 'string') return '?';
  // Drop parenthetical suffixes like "(DHI)" before deriving initials
  const withoutParens = fullName.replace(/\([^)]*\)/g, ' ');
  const parts = withoutParens
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(/[^A-Za-z]/g, ''))
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Power glyph — clearer “end session” signal than a leave/arrow metaphor. */
function IconSignOut({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v8" />
      <path d="M7.5 6.3a7 7 0 1 0 9 0" />
    </svg>
  );
}

function UserMenu({ user, organizationName, onProfileClick, onPreferencesClick, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const menuRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Fetch profile picture with auth (same pattern as ProfileSettings).
  // Bare <img src> cannot send Authorization, so the API returns 401.
  useEffect(() => {
    let cancelled = false;

    const fetchProfilePicture = async () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      if (!user?.profile_picture || !user?.id) {
        setProfilePictureUrl(null);
        return;
      }

      const token = sessionStorage.getItem('token');
      if (!token) {
        setProfilePictureUrl(null);
        return;
      }

      try {
        const response = await axios.get(API_ENDPOINTS.AUTH.PROFILE_PICTURE(user.id), {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });

        if (cancelled) return;

        const blobUrl = URL.createObjectURL(response.data);
        blobUrlRef.current = blobUrl;
        setProfilePictureUrl(blobUrl);
      } catch (error) {
        console.error('Error fetching profile picture for nav avatar:', error);
        if (!cancelled) {
          setProfilePictureUrl(null);
        }
      }
    };

    fetchProfilePicture();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [user?.profile_picture, user?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!user) {
    return null;
  }

  const initials = getInitials(user.full_name);
  const roleLabel = formatRoleName(user.role);
  const showPicture = Boolean(user.profile_picture && profilePictureUrl);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleProfileClick = () => {
    setIsOpen(false);
    onProfileClick();
  };

  const handlePreferencesClick = () => {
    setIsOpen(false);
    if (onPreferencesClick) onPreferencesClick();
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    onLogout();
  };

  const renderAvatar = (sizeClass = '') => (
    <div className={`user-avatar ${sizeClass}`.trim()}>
      {showPicture ? (
        <img
          src={profilePictureUrl}
          alt=""
          onError={() => setProfilePictureUrl(null)}
          className="avatar-img"
        />
      ) : (
        <span className="avatar-initials" aria-hidden="true">{initials}</span>
      )}
    </div>
  );

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        type="button"
        className={`user-menu-trigger avatar-only ${isOpen ? 'open' : ''}`}
        onClick={toggleMenu}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={`${user.full_name} (${roleLabel})`}
      >
        {renderAvatar()}
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`} aria-hidden="true">▼</span>
      </button>

      {isOpen && (
        <div className="user-dropdown" role="menu" aria-label="Account menu">
          <div className="dropdown-identity">
            {renderAvatar('avatar-lg')}
            <div className="dropdown-user-info">
              <div className="dropdown-name">{user.full_name}</div>
              <div className="dropdown-meta">
                <span className={`user-role-chip role-${user.role}`}>{roleLabel}</span>
                {organizationName ? (
                  <span className="dropdown-org" title={organizationName}>{organizationName}</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="dropdown-actions">
            <button type="button" className="dropdown-item" role="menuitem" onClick={handleProfileClick}>
              <span className="dropdown-icon" aria-hidden="true">👤</span>
              <span>Profile Settings</span>
            </button>
            <button type="button" className="dropdown-item" role="menuitem" onClick={handlePreferencesClick}>
              <span className="dropdown-icon" aria-hidden="true">🧩</span>
              <span>Preferences</span>
            </button>
          </div>

          <div className="dropdown-footer">
            <button type="button" className="dropdown-item logout-item" role="menuitem" onClick={handleLogoutClick}>
              <IconSignOut className="dropdown-icon dropdown-icon-svg" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
